import React, { Component } from 'react';
import { createPortal } from 'react-dom';
import { connect } from 'react-redux';
import { getDistanceElapsedTime } from '../utils/elapsed-time';
import '../css/floating-race-panel.css';
import '../css/station-mode.css';
import getRecentRegistrations from '../utils/recent-registrations';
import BackupDifferences from './backup-differences';
import { Pedestrismo, PedestrismoBackup } from '../constants/enums';
import { actionTypes, setTimerElapsed } from '../actions';
import { parseResultTime } from '../utils/backup-differences';
import { formatResultDuration } from '../utils/result-duration';
import { parseTimerInput, adjustedTimerState, timerAdjustmentError } from '../utils/timer-adjustment';

export class FloatingRacePanel extends Component {
  constructor(props) {
    super(props);
    this.state = { collapsed: false, activeSection: null, timerInput: '', timerError: '', timerMessage: '' };
  }

  componentDidMount() {
    this.interval = setInterval(() => this.forceUpdate(), 1000);
  }

  componentWillUnmount() {
    clearInterval(this.interval);
  }

  toggleSection(section) {
    if (section === 'timer' && this.state.activeSection !== 'timer') {
      const { startedAt, stoppedAt, baseTime } = this.props;
      const end = stoppedAt === undefined ? Date.now() : stoppedAt;
      this.setState({ timerInput: formatResultDuration(startedAt ? end - startedAt + (Number(baseTime) || 0) : 0),
        timerError: '', timerMessage: '' });
    }
    this.setState(state => ({ activeSection: state.activeSection === section ? null : section }));
  }

  applyTimerAdjustment(event) {
    event.preventDefault();
    if (this.props.uploading || this.props.copyingResult) return;
    const elapsedMs = parseTimerInput(this.state.timerInput);
    if (elapsedMs === null) {
      this.setState({ timerError: 'Usá HH:mm:ss o HH:mm:ss.SSS, por ejemplo 00:35:12.450.', timerMessage: '' });
      return;
    }
    const now = Date.now();
    const nextTimer = adjustedTimerState(this.props, elapsedMs, now);
    const error = timerAdjustmentError(nextTimer, this.props.participantList || [], this.props.raceInfo || {});
    if (error) {
      this.setState({ timerError: error, timerMessage: '' });
      return;
    }
    this.props.dispatch(setTimerElapsed(elapsedMs, now));
    this.setState({ timerError: '', timerMessage: 'Cronómetro ajustado a ' + formatResultDuration(elapsedMs)
      + '. Los tiempos se recalcularon; volvé a subirlos para publicar el cambio.' });
  }

  handleResultCopied(row, value, eventID, competition) {
    if (eventID !== this.props.eventID || competition !== this.props.raceInfo.competition || !this.props.startedAt) return;
    const time = parseResultTime(value);
    if (!time) return;
    const index = this.props.participantList.findIndex(participant =>
      String(participant.id) === String(row.userID) && String(participant.distanceID) === String(row.distanceID));
    if (index < 0) return;
    this.props.dispatch({ type: actionTypes.EDIT_REGISTER_TIME, index, competition,
      now: Number(this.props.startedAt) + time.milliseconds, partial: 'formattedTime1', deltaTime: 0 });
  }

  render() {
    const { baseTime, startedAt, stoppedAt, distances, uploading, copyingResult, onCopyingChange, status, log = [], distanceOffsets = [], participantList = [], raceInfo = {}, onUpload, eventID } = this.props;
    const recentRegistrations = getRecentRegistrations(participantList, raceInfo, startedAt);
    const now = Date.now();
    const { collapsed, activeSection } = this.state;
    const isBackup = raceInfo.competition === PedestrismoBackup;
    const clockDistances = distances.map(distance => {
      const offset = distance.id === 'general' ? 0 : Number(distanceOffsets[distance.id - 1] || 0);
      return { ...distance, offset,
        elapsed: getDistanceElapsedTime(baseTime, startedAt, stoppedAt, offset, now) };
    });
    // Keep the panel visible even when its Results tab is hidden.
    return createPortal(
      <div className={'floating-race-panel-shell' + (collapsed ? ' floating-race-panel-shell--collapsed' : '') + (isBackup ? ' station-backup' : ' station-primary')}>
      <aside id="floating-race-panel"
        className={'floating-race-panel' + (collapsed ? ' floating-race-panel--collapsed' : '')}
        aria-label="Cronómetro y subida de resultados" aria-hidden={collapsed}
        inert={collapsed ? '' : undefined}>
        <button type="button" className="floating-race-panel__toggle"
          aria-expanded={!collapsed} aria-controls="floating-race-panel"
          onClick={() => this.setState({ collapsed: !collapsed })}>
          <span>{isBackup ? 'Estación Backup · Ocultar' : raceInfo.competition === Pedestrismo ? 'Estación Primaria · Ocultar' : 'Ocultar panel'}</span>
          <span aria-hidden="true"> ▶</span>
        </button>
        <div className="floating-race-panel__clocks">
          {clockDistances.map(distance => <div className="floating-race-panel__clock" key={distance.id}>
            <strong>{distance.label}</strong>
            {distance.offset !== 0 && <small className="floating-race-panel__offset">
              {distance.offset > 0 ? '+' : ''}{distance.offset} min
            </small>}
            <div className="floating-race-panel__time" aria-label={'Tiempo de ' + distance.label}>{distance.elapsed}</div>
          </div>)}
        </div>
        <div id="floating-race-actions" className="floating-race-panel__actions">
          <div className="floating-race-panel__label">Subir resultados</div>
          {distances.map(distance => (
            <button type="button" className={isBackup ? 'btn btn-danger btn-backup' : 'btn btn-primary'} key={distance.id}
              disabled={uploading || copyingResult || !eventID} onClick={() => onUpload(eventID, distance.type)}>
              {distance.label}
            </button>
          ))}
          {distances.length === 0 && <p>Configurá las distancias de la carrera.</p>}
          {!eventID && <p>Seleccioná un evento para subir resultados.</p>}
        </div>
        <div className="floating-race-panel__status" role="status" aria-live="polite">{status}</div>
        <details className="floating-race-panel__timer-editor" open={activeSection === 'timer'}>
          <summary onClick={event => { event.preventDefault(); this.toggleSection('timer'); }}>
            Ajustar cronómetro
          </summary>
          <form className="floating-race-panel__timer-form" onSubmit={event => this.applyTimerAdjustment(event)}>
            <input id="race-timer-value" className="form-control" type="text" placeholder="00:35:12.450"
              value={this.state.timerInput} autoComplete="off" aria-describedby="race-timer-help"
              aria-invalid={Boolean(this.state.timerError)} disabled={!startedAt || uploading || copyingResult}
              onChange={event => this.setState({ timerInput: event.target.value, timerError: '', timerMessage: '' })} />
            <p id="race-timer-help">Ajusta la largada (t0) y recalcula los tiempos de los atletas, conservando sus llegadas.</p>
            {!startedAt && <p>Iniciá el cronómetro para poder ajustarlo.</p>}
            <button type="submit" className={isBackup ? 'btn btn-danger btn-backup' : 'btn btn-primary'}
              disabled={!startedAt || uploading || copyingResult}>Aplicar nuevo tiempo</button>
            {this.state.timerError && <p role="alert">{this.state.timerError}</p>}
            {this.state.timerMessage && <p role="status">{this.state.timerMessage}</p>}
          </form>
        </details>
        <details className="floating-race-panel__recent" open={activeSection === 'recent'}>
          <summary onClick={event => { event.preventDefault(); this.toggleSection('recent'); }}>
            Últimos dorsales ({recentRegistrations.length})
          </summary>
          <div className="floating-race-panel__recent-entries">
            {recentRegistrations.length === 0 ? <p>Todavía no hay llegadas registradas.</p> :
              <table className="floating-race-panel__recent-table">
                <caption className="sr-only">Últimos 15 dorsales registrados, del más reciente al más antiguo</caption>
                <thead><tr><th scope="col">Dorsal</th><th scope="col">Dist.</th><th scope="col">Tiempo</th></tr></thead>
                <tbody>{recentRegistrations.map(item => <tr key={item.key}>
                  <th scope="row">{item.number}</th>
                  <td>{item.distance ? item.distance + 'K' : '—'}</td>
                  <td>{item.time}</td>
                </tr>)}</tbody>
              </table>}
          </div>
        </details>
        {(raceInfo.competition === Pedestrismo || raceInfo.competition === PedestrismoBackup) &&
          <BackupDifferences eventID={eventID} uploading={uploading} visible={!collapsed}
            open={activeSection === 'backup'} onToggle={() => this.toggleSection('backup')}
            competition={raceInfo.competition} onCopyingChange={onCopyingChange}
            onCopied={(...args) => this.handleResultCopied(...args)} />}
        <details className="floating-race-panel__log" open={activeSection === 'messages'}>
          <summary onClick={event => { event.preventDefault(); this.toggleSection('messages'); }}>
            Mensajes ({log.length})
          </summary>
          <div className="floating-race-panel__log-entries">
            {log.length === 0 && <p>Todavía no se realizaron subidas.</p>}
            {log.map(entry => <article className="floating-race-panel__log-entry" key={entry.id}>
              <strong>{entry.startedAt} · {entry.distance}</strong>
              <div>Evento {entry.eventID}</div>
              <p>{entry.message}</p>
              {!entry.pending && <small>Finalizó: {entry.finishedAt}</small>}
            </article>)}
          </div>
        </details>
      </aside>
      {collapsed && <button type="button" className="floating-race-panel__open"
        aria-label="Abrir panel" title="Abrir panel" aria-expanded={false}
        aria-controls="floating-race-panel" onClick={() => this.setState({ collapsed: false })}>
        <span aria-hidden="true">+</span>
      </button>}
      </div>, document.body
    );
  }
}

export default connect(state => ({
  ...state.reducerTimer,
  participantList: state.reducerRegisterTime.participantList,
  raceInfo: state.reducerRegisterInfo,
  distanceOffsets: [1, 2, 3].map(index => state.reducerRegisterInfo['distance' + index + 'StartOffset'])
}))(FloatingRacePanel);



// WEBPACK FOOTER //
// ./src/containers/floating-race-panel.js